/**
 * 💳 PAYMENT SERVICE — Stripe Integration Layer
 *
 * Handles checkout, subscription management, webhooks, and invoices.
 * Ready for Stripe integration — set VITE_STRIPE_PUBLISHABLE_KEY in .env.
 *
 * Components must NOT call Stripe directly — use this service.
 */

import { supabase } from '../lib/supabaseClient';
import type { BillingCycle, PlanSlug } from '../types/saasTypes';

// ─── Types ────────────────────────────────────────────────────

export interface CheckoutSessionParams {
  workspaceId: string;
  planSlug: PlanSlug;
  billingCycle: BillingCycle;
  successUrl?: string;
  cancelUrl?: string;
  couponCode?: string;
}

export interface CheckoutResult {
  success: boolean;
  sessionUrl?: string;
  error?: string;
}

export interface CustomerPortalResult {
  success: boolean;
  portalUrl?: string;
  error?: string;
}

export interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  expiryMonth: number;
  expiryYear: number;
  isDefault: boolean;
}

export interface InvoiceItem {
  description: string;
  quantity: number;
  unitAmount: number;
  totalAmount: number;
}

// ─── Price Mapping ────────────────────────────────────────────

const PRICE_TABLE: Record<PlanSlug, { monthly: number; annual: number }> = {
  free:       { monthly: 0,    annual: 0 },
  starter:    { monthly: 497,  annual: 4956 },
  pro:        { monthly: 1490, annual: 14904 },
  enterprise: { monthly: 0,    annual: 0 }, // custom pricing
};

// Stripe Price IDs — configure in .env or Supabase secrets
const STRIPE_PRICE_IDS: Record<string, string> = {
  'starter_monthly':    import.meta.env.VITE_STRIPE_PRICE_STARTER_MONTHLY    || '',
  'starter_annual':     import.meta.env.VITE_STRIPE_PRICE_STARTER_ANNUAL     || '',
  'pro_monthly':        import.meta.env.VITE_STRIPE_PRICE_PRO_MONTHLY        || '',
  'pro_annual':         import.meta.env.VITE_STRIPE_PRICE_PRO_ANNUAL         || '',
  'enterprise_monthly': import.meta.env.VITE_STRIPE_PRICE_ENTERPRISE_MONTHLY || '',
  'enterprise_annual':  import.meta.env.VITE_STRIPE_PRICE_ENTERPRISE_ANNUAL  || '',
};

/**
 * Product catalog — 3 products × 2 billing cycles = 6 prices.
 * Prices in BRL cents (100 = R$ 1,00).
 */
export const STRIPE_PRODUCTS = {
  starter: {
    name: 'Starter',
    monthly: { brl: 49700 },   // R$ 497,00/mês
    annual:  { brl: 495600 },  // R$ 4.956,00/ano (~R$ 413/mês)
  },
  pro: {
    name: 'Pro',
    monthly: { brl: 149000 },  // R$ 1.490,00/mês
    annual:  { brl: 1490400 }, // R$ 14.904,00/ano (~R$ 1.242/mês)
  },
  enterprise: {
    name: 'Enterprise',
    monthly: { brl: 0 },       // preço customizado
    annual:  { brl: 0 },
  },
} as const;

/**
 * Returns true when at least one Stripe price ID is configured.
 * Use this to gate UI paths that require live payment processing.
 */
export function isStripeConfigured(): boolean {
  return !!STRIPE_PRICE_IDS['starter_monthly'];
}

// ─── Checkout ─────────────────────────────────────────────────

/**
 * Creates a checkout session via Supabase Edge Function.
 * The Edge Function should call Stripe's API server-side.
 */
export async function createCheckoutSession(
  params: CheckoutSessionParams
): Promise<CheckoutResult> {
  const { workspaceId, planSlug, billingCycle, successUrl, cancelUrl, couponCode } = params;

  if (planSlug === 'free') {
    return { success: true };
  }

  const priceKey = `${planSlug}_${billingCycle}`;
  const stripePriceId = STRIPE_PRICE_IDS[priceKey];

  // Stripe not configured — do not simulate success, surface a real error
  if (!stripePriceId) {
    return {
      success: false,
      error:
        'Gateway de pagamento não configurado. ' +
        'Configure as variáveis VITE_STRIPE_PRICE_* ou entre em contato com o administrador.',
    };
  }

  try {
    const { data, error } = await supabase.functions.invoke('create-checkout', {
      body: {
        workspace_id: workspaceId,
        price_id: stripePriceId,
        plan_slug: planSlug,
        billing_cycle: billingCycle,
        success_url: successUrl || `${window.location.origin}/administracao/billing?success=true`,
        cancel_url: cancelUrl || `${window.location.origin}/administracao/billing?cancelled=true`,
        coupon_code: couponCode,
      },
    });

    if (error) {
      // Edge function not deployed or auth issue — fallback to DB upgrade intent
      return await recordUpgradeIntent(workspaceId, planSlug, billingCycle, couponCode);
    }

    if (data?.url) {
      return { success: true, sessionUrl: data.url };
    }

    // Function responded but no URL — fallback
    return await recordUpgradeIntent(workspaceId, planSlug, billingCycle, couponCode);
  } catch (err) {
    // Network / function deployment error — fallback to DB
    return await recordUpgradeIntent(workspaceId, planSlug, billingCycle, couponCode);
  }
}

/**
 * Fallback: when Stripe is not configured, record upgrade intent in DB
 */
async function recordUpgradeIntent(
  workspaceId: string,
  planSlug: PlanSlug,
  billingCycle: BillingCycle,
  couponCode?: string
): Promise<CheckoutResult> {
  const amount = PRICE_TABLE[planSlug]?.[billingCycle] ?? 0;

  const { error } = await supabase.from('invoices').insert({
    workspace_id: workspaceId,
    status: 'pending',
    amount,
    currency: 'BRL',
    due_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    metadata: {
      description: `Upgrade para ${planSlug} (${billingCycle})`,
      line_items: [{
        description: `Plano ${planSlug} — ${billingCycle === 'monthly' ? 'Mensal' : 'Anual'}`,
        quantity: 1,
        unit_amount: amount,
      }],
    },
  });

  if (error) {
    // RLS or configuration issue — Stripe not yet active, show contact path
    return {
      success: true,
      error: 'Solicitação de upgrade registrada. Entre em contato com nossa equipe para finalizar a ativação.',
    };
  }

  // Update subscription plan
  const { data: planRow } = await supabase
    .from('plans')
    .select('id')
    .eq('slug', planSlug)
    .maybeSingle();

  if (planRow?.id) {
    await supabase
      .from('subscriptions')
      .update({
        plan_id: planRow.id,
        billing_cycle: billingCycle,
        amount,
      })
      .eq('workspace_id', workspaceId);
  }

  return {
    success: true,
    error: couponCode
      ? `Upgrade registrado com cupom ${couponCode}. Aguardando confirmação de pagamento.`
      : 'Upgrade registrado. Configure o gateway de pagamento para cobrança automática.',
  };
}

// ─── Customer Portal ──────────────────────────────────────────

/**
 * Opens the Stripe Customer Portal for subscription management.
 */
export async function openCustomerPortal(
  workspaceId: string
): Promise<CustomerPortalResult> {
  try {
    const { data, error } = await supabase.functions.invoke('customer-portal', {
      body: {
        workspace_id: workspaceId,
        return_url: `${window.location.origin}/administracao/billing`,
      },
    });

    if (error || !data?.url) {
      return { success: false, error: 'Portal de pagamento não disponível. A edge function precisa ser deployada.' };
    }

    return { success: true, portalUrl: data.url };
  } catch {
    return { success: false, error: 'Gateway de pagamento não configurado. Contate o suporte.' };
  }
}

// ─── Invoice Management ───────────────────────────────────────

export async function getInvoices(workspaceId: string) {
  const { data, error } = await supabase
    .from('invoices')
    .select('*')
    .eq('workspace_id', workspaceId)
    .order('created_at', { ascending: false });

  if (error) throw new Error(error.message);
  return data ?? [];
}

export async function getInvoiceById(invoiceId: string) {
  const { data, error } = await supabase
    .from('invoices')
    .select('*')
    .eq('id', invoiceId)
    .single();

  if (error) throw new Error(error.message);
  return data;
}

// ─── Coupon Validation ────────────────────────────────────────

export async function validateCoupon(
  code: string,
  planSlug: PlanSlug
): Promise<{ valid: boolean; discount?: number; discountType?: string; error?: string }> {
  const { data, error } = await supabase
    .from('coupons')
    .select('*')
    .eq('code', code.toUpperCase())
    .eq('active', true)
    .single();

  if (error || !data) {
    return { valid: false, error: 'Cupom inválido ou expirado' };
  }

  const now = new Date();
  if (data.valid_until && new Date(data.valid_until) < now) {
    return { valid: false, error: 'Cupom expirado' };
  }

  if (data.max_uses && data.used_count >= data.max_uses) {
    return { valid: false, error: 'Cupom esgotado' };
  }

  if (data.plan_slugs && !data.plan_slugs.includes(planSlug)) {
    return { valid: false, error: 'Cupom não válido para este plano' };
  }

  return {
    valid: true,
    discount: data.discount_value,
    discountType: data.discount_type,
  };
}

// ─── Price Calculation ────────────────────────────────────────

export function calculatePrice(
  planSlug: PlanSlug,
  billingCycle: BillingCycle,
  discount?: { value: number; type: string }
): { subtotal: number; discountAmount: number; total: number } {
  const subtotal = PRICE_TABLE[planSlug]?.[billingCycle] ?? 0;

  let discountAmount = 0;
  if (discount) {
    if (discount.type === 'percentage') {
      discountAmount = subtotal * (discount.value / 100);
    } else {
      discountAmount = Math.min(discount.value, subtotal);
    }
  }

  return {
    subtotal,
    discountAmount,
    total: Math.max(0, subtotal - discountAmount),
  };
}

// ─── Subscription Status ─────────────────────────────────────

export async function cancelSubscription(
  workspaceId: string,
  reason?: string
): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase
    .from('subscriptions')
    .update({
      status: 'cancelled',
      cancelled_at: new Date().toISOString(),
    })
    .eq('workspace_id', workspaceId);

  if (error) return { success: false, error: error.message };

  // Log the cancellation (best-effort)
  const { data: { user } } = await supabase.auth.getUser();
  if (user) {
    await supabase.from('audit_logs').insert({
      user_id: user.id,
      organization_id: workspaceId,
      action: 'cancel_subscription',
      metadata: {
        resource_type: 'subscription',
        resource_id: workspaceId,
        reason: reason || 'User-initiated cancellation',
      },
    }).then(() => {}, () => {});
  }

  return { success: true };
}

export async function reactivateSubscription(
  workspaceId: string
): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase
    .from('subscriptions')
    .update({
      status: 'active',
      cancelled_at: null,
    })
    .eq('workspace_id', workspaceId);

  if (error) return { success: false, error: error.message };
  return { success: true };
}
